import { Package, Store, CreditCard, ShoppingCart, Users, Repeat, ArrowRight } from "lucide-react";
import { ACCENT, CtaLink } from "@/components/ecommerce/shared";
import { trackEvent } from "@/lib/tracking";

const JOURNEY = [
  { Icon: Package, label: "Products", note: "Catalogue, pricing and imagery" },
  { Icon: Store, label: "Storefront", note: "Your professional online store" },
  { Icon: CreditCard, label: "Checkout", note: "Supported payments and delivery" },
  { Icon: ShoppingCart, label: "Marketplace", note: "Amazon, Takealot and Makro" },
  { Icon: Users, label: "Customer", note: "Order received and fulfilled" },
  { Icon: Repeat, label: "Repeat purchase", note: "Follow-up that brings them back" },
];
const STEP_S = 1.2;

function JourneyVisual() {
  return (
    <div className="relative rounded-[28px] border border-zinc-200 bg-white p-6 sm:p-8 shadow-[0_40px_90px_-50px_rgba(22,18,15,0.45)]">
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">The connected commerce journey</div>
      <div className="relative mt-6">
        <span aria-hidden className="absolute left-[25px] top-5 bottom-5 w-px bg-zinc-200">
          <span className="ecom-travel absolute -left-[4px] h-[9px] w-[9px] rounded-full" style={{ background: ACCENT, boxShadow: `0 0 0 5px ${ACCENT}26` }} />
        </span>
        <ol className="relative space-y-2.5" data-testid="ecom-hero-journey" aria-label="Products, storefront, checkout, marketplace, customer and repeat purchase — one connected journey">
          {JOURNEY.map(({ Icon, label, note }, i) => (
            <li key={label} data-testid={`ecom-hero-journey-${i}`} className="ecom-node flex items-center gap-4 rounded-2xl border border-zinc-200 bg-[#FBF7F2] px-2 py-2 pr-4"
              style={{ animationDelay: `${i * STEP_S + 0.3}s` }}>
              <span className="ecom-node-icon relative h-[30px] w-[30px] ml-[2px] rounded-xl flex items-center justify-center text-white shrink-0 bg-[#16120F]" style={{ animationDelay: `${i * STEP_S + 0.3}s` }}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-zinc-900">{label}</span>
                <span className="block text-xs text-zinc-500 truncate">{note}</span>
              </span>
              <span className="font-mono text-[10px] font-bold text-zinc-400">0{i + 1}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="mt-5 flex items-center gap-2 text-xs text-zinc-600">
        <Repeat className="h-3.5 w-3.5" style={{ color: ACCENT }} />
        Every stage connected — from first product listing to the next order.
      </div>
    </div>
  );
}

export default function EcomHero() {
  return (
    <section id="home" aria-labelledby="home-heading" data-testid="ecom-hero-section" className="relative pt-28 pb-20 sm:pt-32 sm:pb-24 bg-[#FBF7F2] overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full blur-[120px] opacity-25" style={{ background: ACCENT }} />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold mb-5" style={{ color: ACCENT }}>Rekisa eCommerce Solutions</div>
          <h1 id="home-heading" data-testid="ecom-hero-h1" className="font-display text-[40px] leading-[1.02] sm:text-[54px] lg:text-[62px] font-black tracking-[-0.035em] text-zinc-900">
            Everything You Need to Sell and Grow Online
          </h1>
          <p data-testid="ecom-hero-subheadline" className="mt-5 text-lg sm:text-xl font-semibold text-zinc-800">
            eCommerce Websites. Strategy. Marketplace Enablement.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Whether you're launching your first online store, looking to improve your existing eCommerce business or preparing to
            sell on marketplaces, Rekisa brings the expertise and systems together.
          </p>
          <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed max-w-2xl">
            We help South African businesses develop their eCommerce strategy, build professional online stores, set up their
            products and selling channels, and improve the customer journey from discovery to repeat purchase.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <CtaLink label="Get My eCommerce Plan" location="ecom_hero" testid="ecom-hero-cta-primary" />
            <a href="#services" onClick={() => trackEvent("cta_click", { location: "ecom_hero_secondary", service: "ecommerce_solutions" })} data-testid="ecom-hero-cta-secondary"
              className="inline-flex items-center gap-1.5 rounded-full min-h-12 px-6 py-3 border border-zinc-300 bg-white text-zinc-900 font-semibold hover:border-zinc-500 transition-colors">
              Explore Our Solutions <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p data-testid="ecom-hero-trust" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-800">
            <span className="h-2 w-2 rounded-full" style={{ background: ACCENT }} />
            Empowering 5,000+ South African SMEs with eCommerce solutions since 2020.
          </p>
        </div>
        <JourneyVisual />
      </div>
    </section>
  );
}
